import { useQuery } from "@apollo/client/react";
import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useSearchParams } from "react-router";

import { constantsData } from "@/data";
import { getRoleMappingsListQuery } from "@/graph";
import { openAlert } from "@/redux/actions";
import { displayErrorHandler } from "@/utils";

const useRoleMappingsList = (clientId, searchValue) => {
    const [params] = useSearchParams();

    const pageParam = parseInt(params.get("page")) || 1;

    const limitParam = parseInt(params.get("limit")) || 25;

    const [page, setPage] = useState(pageParam);

    const [limit, setLimit] = useState(limitParam);

    const { t } = useTranslation();

    const dispatch = useDispatch();

    const { isAuthenticated } = useAuth0();

    const { data, error, loading, previousData, refetch } = useQuery(getRoleMappingsListQuery, {
        skip: !isAuthenticated,
        variables: {
            clientId: clientId || null,
            limit,
            page: page - 1,
            search: searchValue || null,
        },
    });

    useEffect(
        () => {
            if (error?.message === constantsData.failedToFetch && !loading) {
                displayErrorHandler(t("messages.networkFailure"), dispatch, openAlert);
            }
        },
        [error, data], // eslint-disable-line
    );

    // Falls back to the previous result so a search/page change keeps the rows on screen instead of
    // unmounting the view — remounting would reset the search input's internal value.
    const result = data?.listRoleMappings ?? previousData?.listRoleMappings;

    return {
        count: result?.meta?.resultCount,
        data: result?.posts,
        error,
        fetching: loading,
        limit,
        loading: loading && !previousData,
        page: result?.meta?.currentPage,
        refetch,
        setLimit,
        setPage,
    };
};

export default useRoleMappingsList;
